"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Spinner,
} from "flowbite-react";

// Utility: format ISO date/time to a readable local string
const toLocalDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  // Example: Sat, Oct 23, 2025, 8:26 PM (adjust per locale needs)
  return `${d.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  })}, ${d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export default function StackDetailsModal({
  stackPayload,
  open,
  setOpen,
  isFetching,
}) {
  const [view, setView] = useState(null); // formatted details for display
  
  // Build formatted local state when opening the modal
  const openWithData = useCallback(() => {
    const s = stackPayload ?? {};
    const params = s.parameters ?? {};
    const outputs = Array.isArray(s.outputs) ? s.outputs : [];

    const securityGroups = (params.security_groups || "")
      .split(",")
      .filter(Boolean);

    const formatted = {
      // Header
      name: s.stack_name || "—",
      id: s.id || "—",
      owner: s.stack_owner || "—",
      status: s.stack_status || "—",
      statusReason: s.stack_status_reason || "—",

      // Timestamps
      createdAt: toLocalDateTime(s.creation_time),
      updatedAt: toLocalDateTime(s.updated_time),
      deletedAt: toLocalDateTime(s.deletion_time),

      // Links
      selfLink: s.links?.[0]?.href || "—",

      // Parameters (selected/high-signal)
      params: {
        image: params.image || "—",
        flavor: params.flavor || "—",
        network: params.network_id || "—",
        subnet: params.subnet_id || "—",
        keyName: params.key_name || "—",
        vnName: params.vn_name || "—",
        securityGroups,
        appPort: params.app_port || "—",
        asg: {
          desired: params.asg_desired_capacity || "—",
          min: params.asg_min_size || "—",
          max: params.asg_max_size || "—",
          cooldown: params.asg_cooldown || "—",
        },
        scaling: {
          upAdj: params.scale_up_adjustment || "—",
          downAdj: params.scale_down_adjustment || "—",
          cooldown: params.scaling_cooldown || "—",
        },
        cpu: {
          up: params.cpu_scale_up_threshold || "—",
          down: params.cpu_scale_down_threshold || "—",
          granularity: params.cpu_granularity || "—",
          periods: params.cpu_evaluation_periods || "—",
        },
        ram: {
          up: params.ram_scale_up_threshold_percent || params.ram_scale_up_threshold_mb || "—",
          down: params.ram_scale_down_threshold_percent || params.ram_scale_down_threshold_mb || "—",
          granularity: params.ram_granularity || "—",
          periods: params.ram_evaluation_periods || "—",
        },
        lb: {
          protocol: params.lb_protocol || "—",
          port: params.lb_protocol_port || "—",
          algorithm: params.lb_algorithm || "—",
          hcType: params.health_check_type || "—",
          hcDelay: params.health_check_delay || "—",
          hcTimeout: params.health_check_timeout || "—",
          hcRetries: params.health_check_max_retries || "—",
          hcPath: params.health_check_url_path || "—",
          hcExpected: params.health_check_expected_codes || "—",
        },
      },

      // Outputs - Only ASG size, Server IPs, and LB VIP
      outputs: {
        autoscalingGroupSize:
          outputs.find((o) => o.output_key === "autoscaling_group_size")
            ?.output_value ?? "—",
        loadbalancerVip:
          outputs.find((o) => o.output_key === "loadbalancer_vip")
            ?.output_value ?? "—",
        allServerIps:
          Array.isArray(outputs.find((o) => o.output_key === "all_server_ips")?.output_value)
            ? outputs.find((o) => o.output_key === "all_server_ips")?.output_value
            : typeof outputs.find((o) => o.output_key === "all_server_ips")?.output_value === 'string'
            ? outputs.find((o) => o.output_key === "all_server_ips")?.output_value.split(',').filter(Boolean)
            : [],
      },

      // Meta
      description: s.description?.trim() || "—",
      templateDescription: s.template_description?.trim() || "—",
      disableRollback: s.disable_rollback ?? "—",
      capabilities: s.capabilities ?? [],
      tags: s.tags ?? [],
      projectId: params["OS::project_id"] || "—",
      stackIdParam: params["OS::stack_id"] || "—",
      stackNameParam: params["OS::stack_name"] || "—",
    };

    setView(formatted);
  }, [stackPayload]);

  useEffect(() => {
    openWithData();
  }, [openWithData]);

  return (
    <>
      <Modal show={open} onClose={() => setOpen(false)} size="4xl" dismissible>
        {isFetching ? (
          <Spinner />
        ) : (
          <>
            {" "}
            <ModalHeader>
              <div className="flex flex-col gap-1">
                <span className="text-lg font-semibold">{view?.name}</span>
                <span className="text-xs text-gray-500">{view?.id}</span>
              </div>
            </ModalHeader>
            <ModalBody>
              {/* Status */}
              <section className="mb-6">
                <div className="rounded-lg border border-gray-200 p-4">
                  <dl className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Status
                      </dt>
                      <dd className="mt-1 text-sm">{view?.status}</dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Reason
                      </dt>
                      <dd className="mt-1 text-sm break-words">
                        {view?.statusReason}
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>

              {/* Timestamps */}
              <section className="mb-6">
                <div className="rounded-lg bg-gray-50 p-4">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Created
                      </dt>
                      <dd className="mt-1 text-sm">{view?.createdAt}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Updated
                      </dt>
                      <dd className="mt-1 text-sm">{view?.updatedAt}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Deleted
                      </dt>
                      <dd className="mt-1 text-sm">{view?.deletedAt}</dd>
                    </div>
                  </dl>
                </div>
              </section>

              {/* Links 
              
              <section className="mb-6">
                <div className="rounded-lg border border-gray-200 p-4">
                  <dl className="grid grid-cols-1 gap-4">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Self link
                      </dt>
                      <dd className="mt-1 text-sm">
                        {view?.selfLink !== "—" ? (
                          <a
                            href={view?.selfLink}
                            className="text-blue-600 hover:underline break-all"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {view?.selfLink}
                          </a>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>
              
              */}

              {/* Parameters summary */}
              <section className="mb-6">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">
                  Configuration
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <dl className="grid grid-cols-1 gap-3">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Image
                        </dt>
                        <dd className="mt-1 text-sm break-all">
                          {view?.params.image}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Flavor
                        </dt>
                        <dd className="mt-1 text-sm">{view?.params.flavor}</dd>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            Network
                          </dt>
                          <dd className="mt-1 text-sm break-all">
                            {view?.params.network}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            Subnet
                          </dt>
                          <dd className="mt-1 text-sm break-all">
                            {view?.params.subnet}
                          </dd>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            Key name
                          </dt>
                          <dd className="mt-1 text-sm">
                            {view?.params.keyName}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            VN name
                          </dt>
                          <dd className="mt-1 text-sm break-all">
                            {view?.params.vnName}
                          </dd>
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Security groups
                        </dt>
                        <dd className="mt-1 text-sm">
                          {view?.params.securityGroups.length ? (
                            <ul className="list-disc list-inside space-y-1">
                              {view.params.securityGroups.map((sg) => (
                                <li key={sg} className="break-all">
                                  {sg}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          App port
                        </dt>
                        <dd className="mt-1 text-sm">{view?.params.appPort}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <dl className="grid grid-cols-1 gap-3">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          ASG
                        </dt>
                        <dd className="mt-1 text-sm">
                          desired {view?.params.asg.desired}, min{" "}
                          {view?.params.asg.min}, max {view?.params.asg.max},
                          cooldown {view?.params.asg.cooldown}s
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Scaling policy
                        </dt>
                        <dd className="mt-1 text-sm">
                          up {view?.params.scaling.upAdj}, down{" "}
                          {view?.params.scaling.downAdj}, cooldown{" "}
                          {view?.params.scaling.cooldown}s
                        </dd>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            CPU thresholds
                          </dt>
                          <dd className="mt-1 text-sm">
                            up {view?.params.cpu.up !== "—" ? view?.params.cpu.up : "—"}, down{" "}
                            {view?.params.cpu.down !== "—" ? view?.params.cpu.down : "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            CPU eval
                          </dt>
                          <dd className="mt-1 text-sm">
                            gran {view?.params.cpu.granularity}s, periods{" "}
                            {view?.params.cpu.periods}
                          </dd>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            RAM thresholds
                          </dt>
                          <dd className="mt-1 text-sm">
                            up {view?.params.ram.up !== "—" ? `${view?.params.ram.up}` : "—"}, down{" "}
                            {view?.params.ram.down !== "—" ? `${view?.params.ram.down}` : "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">
                            RAM eval
                          </dt>
                          <dd className="mt-1 text-sm">
                            gran {view?.params.ram.granularity}s, periods{" "}
                            {view?.params.ram.periods}
                          </dd>
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Load balancer
                        </dt>
                        <dd className="mt-1 text-sm">
                          {view?.params.lb.protocol}:{view?.params.lb.port} •{" "}
                          {view?.params.lb.algorithm}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">
                          Health check
                        </dt>
                        <dd className="mt-1 text-sm">
                          {view?.params.lb.hcType} {view?.params.lb.hcPath} •{" "}
                          {view?.params.lb.hcExpected} • delay{" "}
                          {view?.params.lb.hcDelay}s • timeout{" "}
                          {view?.params.lb.hcTimeout}s • retries{" "}
                          {view?.params.lb.hcRetries}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </section>

              {/* Outputs */}
              <section className="mb-6">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">
                  Autoscaling Group Details
                </h4>
                <div className="rounded-lg bg-gray-50 p-4">
                  <dl className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        ASG size
                      </dt>
                      <dd className="mt-1 text-sm">
                        {String(view?.outputs.autoscalingGroupSize)}
                      </dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Server IPs
                      </dt>
                      <dd className="mt-1 text-sm">
                        {view?.outputs.allServerIps?.length ? (
                          <ul className="list-disc list-inside space-y-1">
                            {view.outputs.allServerIps.map((ip, index) => (
                              <li key={index} className="break-all">
                                {ip}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        LB VIP
                      </dt>
                      <dd className="mt-1 text-sm break-all">
                        {view?.outputs.loadbalancerVip !== "—" ? String(view?.outputs.loadbalancerVip) : "—"}
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>

              {/* Meta 
              
              <section className="mb-2">
                <div className="rounded-lg border border-gray-200 p-4">
                  <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Description
                      </dt>
                      <dd className="mt-1 text-sm whitespace-pre-wrap">
                        {view?.description}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">
                        Template description
                      </dt>
                      <dd className="mt-1 text-sm whitespace-pre-wrap">
                        {view?.templateDescription}
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>
              
              */}
            </ModalBody>
          </>
        )}
        <ModalFooter>
          <Button
            color="light"
            style={{ color: "white", backgroundColor: "red" }}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
